import React, { useEffect, useState } from "react";
import CourseService from "../../../services/KangarooServices/CourseService";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  Col,
  Container,
  Row,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useNavigate } from "react-router-dom";
import CourseGroupForm from "../component/CourseGroupForm";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast"; // optional drag handle icon
// DnD Kit
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

// ======= Sortable Card =======

const SortableCard = ({ item, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    zIndex: isDragging ? 1000 : "auto",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className="h-100   border"
        style={{
          border: isDragging
            ? "2px dashed #007bff"
            : isOver
            ? "2px solid #28a745"
            : "1px solid #ddd",
          backgroundColor: isDragging ? "#e7f1ff" : isOver ? "#e6ffe6" : "#fff",
          borderRadius: 12,
          boxShadow: isDragging
            ? "0 12px 24px rgba(0,0,0,0.25)"
            : "0 2px 5px rgba(0,0,0,0.1)",
          cursor: "default",
          transition:
            "background-color 150ms ease, box-shadow 150ms ease, border 150ms ease",
        }}
      >
        {/* Only CardBody is draggable */}
        <CardBody {...listeners} {...attributes} style={{ cursor: "grab" }}>
          <h6 className="">{item.headingName}</h6>
          <div
            className="text-muted small"
            dangerouslySetInnerHTML={{ __html: item.description }}
          />
        </CardBody>

        <CardFooter className="d-flex gap-2 justify-content-center">
          <Button size="sm" color="warning" onClick={() => onEdit(item)}>
            <i className="ri-edit-line"></i>
          </Button>
          <Button size="sm" color="danger" onClick={() => onDelete(item)}>
            <i className="ri-delete-bin-5-line"></i>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

// ======= Main Component =======
const CourseGroup = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);

  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);
  const toggleModal = (item) => {
    setSelectedItem(item);
    setIsModalOpen(!isModalOpen);
  };

  const fetchGrp = async () => {
    try {
      const response = await CourseService.getGrpList();
      // Sort by orderIndex
      response.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
      setData(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchGrp();
  }, []);

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };
  const handleDelete = async () => {
    if (!selectedDeleteItem) return;
    try {
      await CourseService.deleteGrp(selectedDeleteItem.id);
      toast.success("Course group deleted successfully");
      await fetchGrp();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete Course");
    } finally {
      toggleDelete();
      setSelectedDeleteItem(null);
    }
  };

  // DnD Kit sensors
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setData((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);

      // Update API orderIndex for each item
      newItems.forEach(async (item, index) => {
        await CourseService.updateGrp(item.id, {
          headingName: item.headingName,
          description: item.description,
          orderIndex: index,
        });
      });

      return newItems;
    });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Course Groups" pageTitle="Course Management" />

        <Row className="mb-3">
          <Col>
            <p className="text-muted mb-2">
              Drag the tiles to reorder the course groups.
            </p>
          </Col>
          <Col>
            <div className="d-flex justify-content-end">
              <Button color="success" onClick={() => toggleModal()}>
                <i className="ri-add-line align-bottom me-1"></i> Add group
              </Button>
            </div>
          </Col>
        </Row>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={data.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: "16px",
              }}
            >
              {data.map((item) => (
                <SortableCard
                  key={item.id}
                  item={item}
                  onEdit={toggleModal}
                  onDelete={handleDeleteClick}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <CourseGroupForm
          isOpen={isModalOpen}
          toggle={toggleModal}
          fetchGrp={fetchGrp}
          selectedItem={selectedItem}
        />
      </Container>

      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDelete}
      />
    </div>
  );
};

export default CourseGroup;
